"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AVAILABLE_LANGUAGES } from "@/lib/constants/languages";
import {
  isEmailFromVerifiedDomain,
  getVerifiedDomainsDisplay,
  VERIFIED_EMAIL_DOMAINS,
} from "@/lib/constants/email-domains";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import CurrencySelect from "@/components/admin/shared/currency-select";
import TimezoneSelect from "@/components/admin/shared/timezone-select";
import IntegrationTypeSelect from "@/components/admin/shared/integration-type-select";
import AddressEditor from "./address-editor";
import AddressEditorV2 from "./address-editor-v2";
import SimpleAddressInput from "./simple-address-input";
import RichTextEditor from "./rich-text-editor";
import AccountingSettingsSection, {
  type AccountingSettings,
} from "./accounting-settings-section";
import { type ReviewQuestionRow } from "@/components/admin/review-questions/review-questions-client";

interface Venue {
  id?: number;
  name: string; // Kept for backward compatibility
  description?: string; // Kept for backward compatibility
  timezone?: string;
  currency?: string;
  status?: string;
  is_visible?: boolean;
  phone?: string;
  email?: string;
  accounting_info?: string;
  integration_type?: string;
  language?: string;
  default_language?: string; // Add default language field
  min_participant?: number;
  max_participant?: number;
  accounting_id?: number | null;
  facebook_pixel_id?: string;
  venue_infos?: Array<{
    lang: string;
    name?: string;
    information: string;
    address?: string;
    terms_and_conditions?: string;
  }>;
}

interface VenueFormProps {
  venue?: Venue;
  onSubmit: (data: Venue) => Promise<any>;
  onCancel?: () => void;
  isLoading?: boolean;
  className?: string;
  reviewQuestions?: ReviewQuestionRow[];
  hasGlobalReviewDefaults?: boolean;
  /** Show and save the accounting system (feature `accounting_settings`). */
  canEditAccounting?: boolean;
  /** Connect the accounting system (OAuth); super admins only. */
  canConnectAccounting?: boolean;
  /** Show the Review questions tab (feature `review_questions`). */
  showReviewTab?: boolean;
  /** Where Cancel goes without an onCancel. */
  cancelHref?: string;
}

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export default function VenueForm({
  venue,
  onSubmit,
  onCancel,
  isLoading: externalLoading = false,
  className = "",
  reviewQuestions = [],
  hasGlobalReviewDefaults = false,
  canEditAccounting = true,
  canConnectAccounting = true,
  showReviewTab = true,
  cancelHref = "/superadmin/venues",
}: VenueFormProps) {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoading = externalLoading || isSubmitting;

  const [venueInfos, setVenueInfos] = useState<
    Array<{
      lang: string;
      name?: string;
      information: string;
      address?: string;
      terms_and_conditions?: string;
    }>
  >([
    {
      lang: "en",
      name: "",
      information: "",
      address: "",
      terms_and_conditions: "",
    },
  ]);

  const [formData, setFormData] = useState<Venue>(() => {
    const defaultData = {
      name: "",
      description: "",
      timezone: "",
      currency: "",
      status: "draft",
      is_visible: true,
      phone: "",
      email: "",
      accounting_info: "",
      integration_type: "",
      language: "en",
      default_language: "en", // Set English as default language
      min_participant: 1,
      max_participant: 100,
    };

    if (venue) {
      // Ensure default_language is set, fallback to language field or 'en'
      const venueData = {
        ...defaultData,
        ...venue,
        default_language: venue.default_language || venue.language || "en",
        is_visible: venue.is_visible !== undefined ? venue.is_visible : true,
      };
      return venueData;
    }

    return defaultData;
  });

  const [accountingSettings, setAccountingSettings] =
    useState<AccountingSettings>({
      accounting_system: null,
      organization_id: null,
      cost_center: null,
      access_token: null,
      client_id: null,
      client_secret: null,
      auto_invoice_enabled: false,
    });

  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("general");
  const [activeLanguageTab, setActiveLanguageTab] = useState("en");

  const isEditMode = !!venue?.id;

  useEffect(() => {
    if (venue) {
      setFormData({ ...venue });

      // Initialize venueInfos with multi-language data
      if (venue.venue_infos && venue.venue_infos.length > 0) {
        // Map description field to information for MultilangEditor
        const mappedInfos = venue.venue_infos.map((info: any) => ({
          lang: info.lang,
          name: info.name,
          information: info.description || info.information || "",
          address: info.address || "",
          terms_and_conditions: info.terms_and_conditions || "",
        }));
        setVenueInfos(mappedInfos);
        // Set active language tab to first available language
        if (mappedInfos.length > 0) {
          setActiveLanguageTab(mappedInfos[0].lang);
        }
      } else if (venue.name || venue.description) {
        // Backward compatibility: if old single-language data exists
        setVenueInfos([
          {
            lang: "en",
            name: venue.name || "",
            information: venue.description || "",
            address: "",
            terms_and_conditions: "",
          },
        ]);
        setActiveLanguageTab("en");
      }

      // Initialize accounting settings from venue data
      if (venue.accounting_info) {
        setAccountingSettings((prev) => ({
          ...prev,
          accounting_system: venue.accounting_info || null,
        }));
      }

      // Load additional accounting settings if venue exists
      if (venue.id) {
        fetch(`/api/superadmin/venue/${venue.id}/accounting-settings`)
          .then((res) => res.json())
          .then((data) => {
            if (data.success && data.settings) {
              setAccountingSettings(data.settings);
            } else if (venue.accounting_info) {
              // If API doesn't return settings, at least keep the accounting system from venue data
              setAccountingSettings((prev) => ({
                ...prev,
                accounting_system: venue.accounting_info || null,
              }));
            }
          })
          .catch((error) => {
            console.error("Error loading accounting settings:", error);
            // On error, still try to use the accounting_info from venue data
            if (venue.accounting_info) {
              setAccountingSettings((prev) => ({
                ...prev,
                accounting_system: venue.accounting_info || null,
              }));
            }
          });
      }
    }
    setError(null);
  }, [venue]);

  const handleInputChange = (
    field: keyof Venue,
    value: string | number | boolean,
  ) => {
    // Convert to number for participant fields
    if (field === "min_participant" || field === "max_participant") {
      const numValue = value === "" ? undefined : parseInt(value as string, 10);
      setFormData((prev) => ({ ...prev, [field]: numValue }));
    } else {
      // When language is updated, also update default_language
      if (field === "language") {
        setFormData((prev) => ({
          ...prev,
          [field]: value,
          default_language: value as string,
        }));
      } else {
        setFormData((prev) => ({ ...prev, [field]: value }));
      }
    }
  };

  const handleVenueInfosChange = (
    infos: Array<{
      lang: string;
      name?: string;
      information: string;
      address?: string;
    }>,
  ) => {
    setVenueInfos(infos);
    // Update the backward-compatible fields with English data
    const englishInfo = infos.find((info) => info.lang === "en");
    if (englishInfo) {
      setFormData((prev) => ({
        ...prev,
        name: englishInfo.name || "",
        description: englishInfo.information || "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation - check if English name and description are provided
    const englishInfo = venueInfos.find((info) => info.lang === "en");
    if (!englishInfo || !englishInfo.name?.trim()) {
      setError("Venue name (English) is required");
      return;
    }
    if (!englishInfo.information?.trim()) {
      setError("Venue description (English) is required");
      return;
    }

    // Validate email domain - must be from one of the verified domains
    if (formData.email && !isEmailFromVerifiedDomain(formData.email)) {
      setError(
        `Email must be from one of the verified domains: ${getVerifiedDomainsDisplay()}`,
      );
      return;
    }

    // Validate participant numbers
    if (formData.min_participant && formData.max_participant) {
      if (formData.min_participant > formData.max_participant) {
        setError(
          "Minimum participants cannot be greater than maximum participants",
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const dataToSubmit = {
        ...formData,
        default_language: formData.language, // Ensure default_language matches the selected language
        venue_infos: venueInfos,
        // Include accounting system selection
        accounting_info:
          accountingSettings.accounting_system || formData.accounting_info,
      };

      const result = await onSubmit(dataToSubmit);
      if (result && result.error) {
        setError(result.error);
        toast.error(result.error);
      } else {
        // Save accounting settings if venue ID is available
        const venueIdToUse = isEditMode ? venue?.id : result?.id;
        if (
          canEditAccounting &&
          venueIdToUse &&
          accountingSettings.accounting_system &&
          accountingSettings.organization_id &&
          accountingSettings.cost_center
        ) {
          try {
            const accountingResponse = await fetch(
              `/api/superadmin/venue/${venueIdToUse}/accounting-settings`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(accountingSettings),
              },
            );

            if (!accountingResponse.ok) {
              console.error("Failed to save accounting settings");
            }
          } catch (accountingError) {
            console.error("Error saving accounting settings:", accountingError);
          }
        }

        // Success - show toast message
        toast.success(
          isEditMode
            ? "Venue updated successfully!"
            : "Venue created successfully!",
        );

        // If it's a new venue and the result contains the new venue ID, redirect
        if (!isEditMode && result?.id) {
          setTimeout(() => {
            window.location.href = `/superadmin/venues/${result.id}`;
          }, 1000);
        } else if (
          isEditMode &&
          canEditAccounting &&
          accountingSettings.accounting_system &&
          accountingSettings.organization_id &&
          accountingSettings.cost_center
        ) {
          // If we saved accounting settings in edit mode, refresh to show OAuth section
          // Use router.refresh() to refetch server component data without full page reload
          setTimeout(() => {
            router.refresh();
          }, 500);
        }
      }
    } catch (err: any) {
      const errorMessage =
        err.message || "An error occurred while saving the venue";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      // Default behavior: redirect to venues list
      window.location.href = cancelHref;
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={className}
      autoComplete="off"
      suppressHydrationWarning
    >
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          {/* Tabs */}
          <div className="border-b mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                key="tab-general"
                type="button"
                onClick={() => setActiveTab("general")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "general"
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                data-tab="general"
              >
                General
              </button>
              <button
                key="tab-content"
                type="button"
                onClick={() => setActiveTab("content")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "content"
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                data-tab="content"
              >
                Content & Location
              </button>
              <button
                key="tab-regional"
                type="button"
                onClick={() => setActiveTab("regional")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "regional"
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                data-tab="regional"
              >
                Regional
              </button>
              <button
                key="tab-financial"
                type="button"
                onClick={() => setActiveTab("financial")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "financial"
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                data-tab="financial"
              >
                Financial
              </button>
              <button
                key="tab-terms"
                type="button"
                onClick={() => setActiveTab("terms")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "terms"
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                data-tab="terms"
              >
                Terms & Conditions
              </button>
              {isEditMode && venue?.id && showReviewTab && (
                <button
                  key="tab-review"
                  type="button"
                  onClick={() => setActiveTab("review")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "review"
                      ? "border-purple-600 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                  data-tab="review"
                >
                  Review questions
                </button>
              )}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === "general" && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>General settings</CardTitle>
                  <CardDescription>
                    Configure basic operational settings for your venue
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="min_participant">
                        Minimum participants
                      </Label>
                      <Input
                        id="min_participant"
                        type="number"
                        min="1"
                        value={formData.min_participant || ""}
                        onChange={(e) =>
                          handleInputChange("min_participant", e.target.value)
                        }
                        placeholder="Enter minimum participants"
                      />
                      <p className="text-xs text-muted-foreground">
                        The minimum number of participants allowed for bookings
                        at this venue
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="max_participant">
                        Maximum participants
                      </Label>
                      <Input
                        id="max_participant"
                        type="number"
                        min="1"
                        value={formData.max_participant || ""}
                        onChange={(e) =>
                          handleInputChange("max_participant", e.target.value)
                        }
                        placeholder="Enter maximum participants"
                      />
                      <p className="text-xs text-muted-foreground">
                        The maximum number of participants allowed for bookings
                        at this venue
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) =>
                        handleInputChange("status", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Visibility Checkbox */}
                  <div className="flex items-start space-x-3 py-2">
                    <Checkbox
                      id="is_visible"
                      checked={formData.is_visible}
                      onCheckedChange={(checked) =>
                        handleInputChange("is_visible", checked === true)
                      }
                      className="mt-1"
                    />
                    <div className="space-y-1">
                      <Label
                        htmlFor="is_visible"
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        Public Visibility
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        When unchecked, venue is hidden from public selection
                        but still accessible via direct URL (for testing)
                      </p>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-sm font-medium mb-4">
                      Contact information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) =>
                            handleInputChange("phone", e.target.value)
                          }
                          placeholder="Enter phone number"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <div className="space-y-1">
                          <Input
                            id="email"
                            type="email"
                            value={formData.email || ""}
                            onChange={(e) => {
                              handleInputChange("email", e.target.value);
                            }}
                            placeholder="venue@domain.com"
                          />
                          <p className="text-xs text-muted-foreground">
                            Allowed domains: {getVerifiedDomainsDisplay()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Facebook Pixel Configuration */}
                  <div className="border-t pt-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="facebookPixelId">Facebook Pixel ID</Label>
                      <Input
                        id="facebookPixelId"
                        type="text"
                        value={formData.facebook_pixel_id || ""}
                        onChange={(e) =>
                          handleInputChange("facebook_pixel_id", e.target.value)
                        }
                        placeholder="15-16 digit Pixel ID for server-side tracking"
                      />
                      <p className="text-xs text-muted-foreground">
                        Optional. Enable server-side Facebook event tracking for
                        bookings and customer interactions.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Content & Location Tab */}
          {activeTab === "content" && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Content & location</CardTitle>
                  <CardDescription>
                    Manage venue information and physical location
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Venue Names, Descriptions and Addresses */}
                  <div className="space-y-4">
                    <div className="border-b">
                      <nav className="-mb-px flex space-x-6">
                        {venueInfos.map((info) => {
                          const language = AVAILABLE_LANGUAGES.find(
                            (l) => l.code === info.lang,
                          );
                          return (
                            <button
                              key={`lang-tab-${info.lang}`}
                              type="button"
                              onClick={() => setActiveLanguageTab(info.lang)}
                              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeLanguageTab === info.lang
                                  ? "border-purple-600 text-purple-600"
                                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                              }`}
                              data-lang={info.lang}
                            >
                              {language?.name || info.lang.toUpperCase()} (
                              {info.lang})
                            </button>
                          );
                        })}

                        {/* Add Language Button */}
                        {venueInfos.length < AVAILABLE_LANGUAGES.length && (
                          <div className="relative group">
                            <button
                              type="button"
                              className="py-2 px-3 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium text-sm"
                              title="Add language"
                            >
                              + Add language
                            </button>

                            {/* Dropdown menu for available languages */}
                            <div className="absolute top-full left-0 mt-1 bg-white border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                              <div className="py-1">
                                {AVAILABLE_LANGUAGES.filter(
                                  (lang) =>
                                    !venueInfos.some(
                                      (info) => info.lang === lang.code,
                                    ),
                                ).map((lang) => (
                                  <button
                                    key={`add-lang-${lang.code}`}
                                    type="button"
                                    onClick={() => {
                                      const newInfo = {
                                        lang: lang.code,
                                        name: "",
                                        information: "",
                                        address: "",
                                        terms_and_conditions: "",
                                      };
                                      setVenueInfos([...venueInfos, newInfo]);
                                      setActiveLanguageTab(lang.code);
                                    }}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    data-add-lang={lang.code}
                                  >
                                    {lang.name} ({lang.code})
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </nav>
                    </div>

                    {/* Content for active language */}
                    {venueInfos.map((info, index) => {
                      if (info.lang !== activeLanguageTab) return null;

                      return (
                        <div key={info.lang} className="space-y-6">
                          {/* Remove Language Button - only show for non-English languages */}
                          {info.lang !== "en" && venueInfos.length > 1 && (
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updatedInfos = venueInfos.filter(
                                    (_, i) => i !== index,
                                  );
                                  setVenueInfos(updatedInfos);
                                  // Switch to English tab if removing current language
                                  if (info.lang === activeLanguageTab) {
                                    setActiveLanguageTab("en");
                                  }
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                Remove{" "}
                                {AVAILABLE_LANGUAGES.find(
                                  (l) => l.code === info.lang,
                                )?.name || info.lang}{" "}
                                ({info.lang})
                              </Button>
                            </div>
                          )}

                          {/* Venue name */}
                          <div className="space-y-2">
                            <Label htmlFor={`name-${info.lang}`}>
                              Venue name *
                            </Label>
                            <Input
                              id={`name-${info.lang}`}
                              value={info.name || ""}
                              onChange={(e) => {
                                const updatedInfos = [...venueInfos];
                                updatedInfos[index] = {
                                  ...info,
                                  name: e.target.value,
                                };
                                handleVenueInfosChange(updatedInfos);
                              }}
                              placeholder={`Enter venue name`}
                            />
                          </div>

                          {/* Venue description */}
                          <div className="space-y-2">
                            <Label htmlFor={`description-${info.lang}`}>
                              Venue description *
                            </Label>
                            <Textarea
                              id={`description-${info.lang}`}
                              value={info.information || ""}
                              onChange={(e) => {
                                const updatedInfos = [...venueInfos];
                                updatedInfos[index] = {
                                  ...info,
                                  information: e.target.value,
                                };
                                handleVenueInfosChange(updatedInfos);
                              }}
                              placeholder={`Enter venue description`}
                              rows={4}
                            />
                          </div>

                          {/* Venue address */}
                          <div className="space-y-2">
                            <Label htmlFor={`address-${info.lang}`}>
                              Venue address
                            </Label>
                            <AddressEditorV2
                              value={info.address || ""}
                              onChange={(content) => {
                                const updatedInfos = [...venueInfos];
                                updatedInfos[index] = {
                                  ...info,
                                  address: content,
                                };
                                handleVenueInfosChange(updatedInfos);
                              }}
                              placeholder="Enter venue address..."
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Regional Settings Tab */}
          {activeTab === "regional" && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Regional settings</CardTitle>
                  <CardDescription>
                    Configure timezone and language for your venue
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <TimezoneSelect
                      value={formData.timezone}
                      onChange={(value) => handleInputChange("timezone", value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="language">Default language</Label>
                    <Select
                      value={formData.language}
                      onValueChange={(value) =>
                        handleInputChange("language", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        {venueInfos.map((info) => {
                          const language = AVAILABLE_LANGUAGES.find(
                            (l) => l.code === info.lang,
                          );
                          return (
                            <SelectItem key={info.lang} value={info.lang}>
                              {language?.name || info.lang.toUpperCase()} (
                              {info.lang})
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Only languages with venue content are available. Add more
                      languages in the Content & Location tab.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Financial Settings Tab */}
          {activeTab === "financial" && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Financial settings</CardTitle>
                  <CardDescription>
                    Configure currency and accounting information for your venue
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <CurrencySelect
                      value={formData.currency}
                      onChange={(value) => handleInputChange("currency", value)}
                    />
                  </div>

                  {/* Accounting Configuration */}
                  {canEditAccounting && (
                    <AccountingSettingsSection
                      value={accountingSettings}
                      onChange={setAccountingSettings}
                      venueId={venue?.id}
                      accountingId={venue?.accounting_id}
                      canConnect={canConnectAccounting}
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Terms & Conditions Tab */}
          {activeTab === "terms" && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Terms & Conditions</CardTitle>
                  <CardDescription>
                    Configure terms and conditions that customers must accept
                    when making bookings. You can provide different versions for
                    each language.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Language selector (same as Content & Location tab) */}
                  <div className="border-b mb-6">
                    <nav className="-mb-px flex space-x-4 overflow-x-auto">
                      {venueInfos.map((info) => (
                        <button
                          key={`terms-lang-${info.lang}`}
                          type="button"
                          onClick={() => setActiveLanguageTab(info.lang)}
                          className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${
                            activeLanguageTab === info.lang
                              ? "border-purple-600 text-purple-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                          }`}
                          data-lang={info.lang}
                        >
                          {AVAILABLE_LANGUAGES.find((l) => l.code === info.lang)
                            ?.name || info.lang.toUpperCase()}{" "}
                          ({info.lang})
                        </button>
                      ))}
                    </nav>
                  </div>

                  {/* Terms Content for active language */}
                  {venueInfos.map((info, index) => {
                    if (info.lang !== activeLanguageTab) return null;

                    return (
                      <div key={`terms-${info.lang}`} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor={`terms-${info.lang}`}>
                            Terms & Conditions Content (
                            {AVAILABLE_LANGUAGES.find(
                              (l) => l.code === info.lang,
                            )?.name || info.lang}
                            )
                          </Label>
                          <RichTextEditor
                            value={info.terms_and_conditions || ""}
                            onChange={(content) => {
                              const updatedInfos = [...venueInfos];
                              updatedInfos[index] = {
                                ...info,
                                terms_and_conditions: content,
                              };
                              handleVenueInfosChange(updatedInfos);
                            }}
                            placeholder={`Enter your venue's terms and conditions in ${AVAILABLE_LANGUAGES.find((l) => l.code === info.lang)?.name || info.lang}...`}
                          />
                          <p className="text-xs text-muted-foreground">
                            Include your venue's booking policies, cancellation
                            terms, payment conditions, and any other legal
                            requirements. This text will be displayed to
                            customers during the booking process in their
                            selected language.
                          </p>
                        </div>

                        {/* Preview */}
                        {info.terms_and_conditions && (
                          <div className="p-4 bg-muted rounded-lg">
                            <h4 className="text-sm font-semibold mb-2">
                              Preview (
                              {AVAILABLE_LANGUAGES.find(
                                (l) => l.code === info.lang,
                              )?.name || info.lang}
                              )
                            </h4>
                            <div
                              className="terms-preview text-sm text-muted-foreground max-h-48 overflow-y-auto max-w-none [&_p]:mb-3 [&_h1]:mb-2 [&_h2]:mb-2 [&_h3]:mb-2 [&_h4]:mb-2"
                              dangerouslySetInnerHTML={{
                                __html: info.terms_and_conditions,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "review" &&
            isEditMode &&
            venue?.id &&
            showReviewTab && (
              <div className="space-y-4">
                <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  <p className="font-medium">
                    Review questions are now configured per show.
                  </p>
                  <p className="mt-1">
                    Open a show (Shows → select a show → “Review questions” tab)
                    to edit the QR review/feedback questions for that show. This
                    venue-level editor has been retired.
                  </p>
                </div>
              </div>
            )}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Form Actions — hidden on the Review questions tab (it saves itself) */}
      <div
        className={`flex justify-end gap-4 mt-6 ${
          activeTab === "review" ? "hidden" : ""
        }`}
      >
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              {isEditMode ? "Update venue" : "Create venue"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
